/**
 * Типизированные обёртки над localStorage / sessionStorage.
 * - localStorage — несекретный профиль (для офлайн boot) и флаги настроек.
 * - refresh-токен хранится сервером только в HttpOnly cookie.
 * - sessionStorage — флаг разблокировки PIN на текущую вкладку.
 */

const KEYS = {
  user: 'jb:user',
  pinUnlocked: 'jb:pin_unlocked',
  notificationsEnabled: 'jb:notifications',
  language: 'jb:lang',
  theme: 'jb:theme',
  calcShowImages: 'jb:calc_show_images',
  calcLayoutMode: 'jb:calc_layout_mode',
  orderLayoutMode: 'jb:order_layout_mode',
  overdueHighlight: 'jb:overdue_highlight',
} as const;

// Одноразовая миграция со старых сборок: refresh больше не должен быть доступен JS.
try { localStorage.removeItem('jb:refresh_token'); } catch { /* storage может быть недоступен */ }

/** Admin-sidebar принадлежит серверному профилю и Field не использует его.
 * Не дублируем эти ключи в офлайн-кеше мобильного приложения. */
export function profileForLocalCache<T>(value: T): T {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  const record = value as Record<string, unknown>;
  const rawPrefs = record.uiPrefs;
  if (!rawPrefs || typeof rawPrefs !== 'object' || Array.isArray(rawPrefs)) return value;
  const prefs = rawPrefs as Record<string, unknown>;
  if (!Object.prototype.hasOwnProperty.call(prefs, 'sidebarFavoritePaths')
      && !Object.prototype.hasOwnProperty.call(prefs, 'sidebarLayout')) return value;
  const { sidebarFavoritePaths: _favorites, sidebarLayout: _layout, ...cachedPrefs } = prefs;
  return { ...record, uiPrefs: cachedPrefs } as T;
}

/**
 * Режим формы замера:
 *  - 'wizard'    — пошаговый мастер с фотокарточками (default)
 *  - 'linear'    — все поля одним списком сверху вниз, выбор через bottom-sheet
 *  - 'accordion' — свёрнутые группы (Тип/материалы, Механизм, Размеры, Заметка)
 *  - 'cards'     — карточки окон + чипы (несколько окон сразу)
 */
export type CalcLayoutMode = 'wizard' | 'linear' | 'accordion' | 'cards';
const VALID_LAYOUTS: CalcLayoutMode[] = ['wizard', 'linear', 'accordion', 'cards'];

/**
 * Вид карточки заказа:
 *  - 'single' — всё одной лентой (как было, default)
 *  - 'tabs2'  — 2 вкладки: Основное / Расчёт
 *  - 'tabs3'  — 3 вкладки: Заказ / Расчёт / Фото и оплата
 *  - 'tabs4'  — 4 вкладки: Заказ / Расчёт / Фото / Оплата
 */
export type OrderLayoutMode = 'single' | 'tabs2' | 'tabs3' | 'tabs4';
const VALID_ORDER_LAYOUTS: OrderLayoutMode[] = ['single', 'tabs2', 'tabs3', 'tabs4'];

export type ThemeId = 'warm' | 'cool';
const VALID_THEMES: ThemeId[] = ['warm', 'cool'];
/** Тема по умолчанию — дневная. Дублируется inline-скриптом в index.html:
 *  при правке менять оба места. */
export const DEFAULT_THEME: ThemeId = 'cool';
/** Старая тема `daylight` была убрана — нормализуем её к 'cool' (тоже светлой),
 *  чтобы пользователи с сохранённой `daylight` не получали поломанную тему. */
const LEGACY_THEME_MAP: Record<string, ThemeId> = { daylight: 'cool' };

export const persistent = {
  getUser: <T>(): T | null => {
    const raw = localStorage.getItem(KEYS.user);
    if (!raw) return null;
    try { return profileForLocalCache(JSON.parse(raw) as T); } catch { return null; }
  },
  setUser: <T>(v: T) => localStorage.setItem(KEYS.user, JSON.stringify(profileForLocalCache(v))),
  clearUser: () => localStorage.removeItem(KEYS.user),

  getNotificationsEnabled: () => localStorage.getItem(KEYS.notificationsEnabled) !== '0',
  setNotificationsEnabled: (v: boolean) => localStorage.setItem(KEYS.notificationsEnabled, v ? '1' : '0'),

  getLanguage: () => (localStorage.getItem(KEYS.language) as 'ru' | 'uz' | null) ?? 'ru',
  setLanguage: (v: 'ru' | 'uz') => localStorage.setItem(KEYS.language, v),

  getTheme: (): ThemeId => {
    const raw = localStorage.getItem(KEYS.theme);
    if (!raw) return DEFAULT_THEME;
    if (VALID_THEMES.includes(raw as ThemeId)) return raw as ThemeId;
    const legacy = LEGACY_THEME_MAP[raw];
    if (legacy) {
      // Перезаписываем устаревшее значение, чтобы при следующем чтении
      // не делать миграцию заново.
      localStorage.setItem(KEYS.theme, legacy);
      return legacy;
    }
    return DEFAULT_THEME;
  },
  setTheme: (v: ThemeId) => localStorage.setItem(KEYS.theme, v),

  // Картинки в калькуляторе (Step1Type, Step3Fabric, Step5Mechanism, Step6Cornice).
  // По умолчанию включены — клиент попросил возможность скрыть для компактного вида.
  getCalcShowImages: () => localStorage.getItem(KEYS.calcShowImages) !== '0',
  setCalcShowImages: (v: boolean) => localStorage.setItem(KEYS.calcShowImages, v ? '1' : '0'),

  // Режим формы замера: wizard/linear/accordion/cards. По умолчанию wizard.
  getCalcLayoutMode: (): CalcLayoutMode => {
    const raw = localStorage.getItem(KEYS.calcLayoutMode);
    if (raw && VALID_LAYOUTS.includes(raw as CalcLayoutMode)) return raw as CalcLayoutMode;
    return 'wizard';
  },
  setCalcLayoutMode: (v: CalcLayoutMode) => localStorage.setItem(KEYS.calcLayoutMode, v),

  getOrderLayoutMode: (): OrderLayoutMode => {
    const raw = localStorage.getItem(KEYS.orderLayoutMode);
    if (raw && VALID_ORDER_LAYOUTS.includes(raw as OrderLayoutMode)) return raw as OrderLayoutMode;
    return 'single';
  },
  setOrderLayoutMode: (v: OrderLayoutMode) => localStorage.setItem(KEYS.orderLayoutMode, v),

  // Подсветка просроченных замеров (красная рамка + статичный красный бейдж).
  // По умолчанию включена; сотрудник может отключить её в профиле.
  getOverdueHighlight: () => localStorage.getItem(KEYS.overdueHighlight) !== '0',
  setOverdueHighlight: (v: boolean) => localStorage.setItem(KEYS.overdueHighlight, v ? '1' : '0'),
};

export const ephemeral = {
  isPinUnlocked: () => sessionStorage.getItem(KEYS.pinUnlocked) === '1',
  unlockPin: () => sessionStorage.setItem(KEYS.pinUnlocked, '1'),
  resetPin: () => sessionStorage.removeItem(KEYS.pinUnlocked),
};

export function clearAuth() {
  persistent.clearUser();
  ephemeral.resetPin();
}
